const OFFICIAL_LABEL = 'Image du produit'
const CAPTURE_LABEL = 'Photographie originale'

export function readItemMeta(item) {
  const raw = item?.item_meta
  if (!raw || typeof raw !== 'object') return {}
  return raw
}

export function isSupabaseObjectUrl(value) {
  const url = String(value || '').toLowerCase()
  return /\/storage\/v1\/object\/(public|sign)\//.test(url) || /\/api\/media\?/.test(url)
}

export function isSignedDisplayUrl(value) {
  const url = String(value || '').toLowerCase()
  return /\/storage\/v1\/object\/sign\//.test(url) || /[?&]token=/.test(url) || /\/api\/media\?/.test(url)
}

export function isPersistentImageUrl(value) {
  if (value && typeof value === 'object') return false
  const url = String(value || '').trim()
  if (!url) return false
  if (/^(blob:|data:|javascript:|vbscript:)/i.test(url)) return false
  if (isSupabaseObjectUrl(url)) return false
  if (/^https?:\/\//i.test(url)) return true
  if (url.startsWith('/') && !url.startsWith('//')) return true
  return !url.includes(':')
}

export function isRelativeStorageUrl(value) {
  const url = String(value || '').trim()
  return url.startsWith('/object/') || url.startsWith('/storage/v1/') || url.startsWith('/api/media')
}

export function isDisplayableImageUrl(value) {
  if (value && typeof value === 'object') return false
  const url = String(value || '').trim()
  if (!url || /^(javascript|vbscript):/i.test(url)) return false
  if (isRelativeStorageUrl(url)) return false
  if (isSignedDisplayUrl(url)) return true
  return isPersistentImageUrl(url) || /^(blob:|data:image\/)/i.test(url)
}

export function persistImageUrl(value) {
  return isPersistentImageUrl(value) ? String(value).trim() : ''
}

export function httpsDisplayUrl(value) {
  const url = String(value || '').trim()
  if (!url || isRelativeStorageUrl(url)) return ''
  if (url.startsWith('http://')) return `https://${url.slice(7)}`
  return url
}

export function storageKeyFromRef(value) {
  const text = String(value || '').trim()
  if (!text) return ''
  if (!/^https?:\/\//i.test(text) && !text.includes('..') && !text.startsWith('/') && /^[A-Za-z0-9._-]+(?:\/[A-Za-z0-9._-]+){1,8}$/.test(text)) {
    return text
  }
  const url = text.split('?')[0]
  const lowered = url.toLowerCase()
  const markers = [
    '/storage/v1/object/public/product-images/',
    '/storage/v1/object/sign/product-images/',
  ]
  const marker = markers.find((entry) => lowered.includes(entry))
  if (!marker) return ''
  try {
    const host = new URL(url).hostname.toLowerCase()
    if (!host.endsWith('.supabase.co') && !host.endsWith('.supabase.in')) return ''
  } catch {
    return ''
  }
  const key = url.slice(lowered.indexOf(marker) + marker.length).replace(/^\/+/, '')
  return /^[A-Za-z0-9._-]+(?:\/[A-Za-z0-9._-]+){1,8}$/.test(key) ? key : ''
}

export function applyStoreToMeta(meta, store) {
  const next = { ...(meta && typeof meta === 'object' ? meta : {}) }
  if (!store || typeof store !== 'object') {
    delete next.store
    delete next.store_name
    delete next.store_address
    delete next.location_recorded_at
    next.latitude = null
    next.longitude = null
    next.location_accuracy = null
    return next
  }
  const name = String(store.name || '').trim()
  const address = String(store.address || '').trim()
  const lat = store.lat == null || store.lat === '' ? null : Number(store.lat)
  const lng = store.lng == null || store.lng === '' ? null : Number(store.lng)
  const accuracy = store.accuracy == null || store.accuracy === '' ? null : Number(store.accuracy)
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng)
  if (!name && !address && !hasCoords) return applyStoreToMeta(next, null)
  const recordedAt = store.recordedAt
    || next.location_recorded_at
    || (next.store && typeof next.store === 'object' ? next.store.recordedAt : '')
    || new Date().toISOString()
  next.store_name = name
  next.store_address = address
  next.latitude = hasCoords ? lat : null
  next.longitude = hasCoords ? lng : null
  next.location_accuracy = hasCoords && Number.isFinite(accuracy) ? accuracy : null
  next.location_recorded_at = recordedAt
  next.store = {
    name,
    address,
    lat: next.latitude,
    lng: next.longitude,
    accuracy: next.location_accuracy,
    placeId: String(store.placeId || ''),
    provider: String(store.provider || (hasCoords ? 'gps' : '')),
    recordedAt,
    mode: String(store.mode || (store.placeId ? 'confirmed' : hasCoords ? 'coords-only' : 'manual')),
  }
  return next
}

function firstFinite(...values) {
  for (const value of values) {
    if (value == null || value === '') continue
    const number = Number(value)
    if (Number.isFinite(number)) return number
  }
  return null
}

export function readItemStore(item) {
  const meta = readItemMeta(item)
  const nested = meta.store && typeof meta.store === 'object' ? meta.store : {}
  const name = String(
    nested.name
    || meta.store_name
    || item?.store_name
    || nested.display_name
    || '',
  ).trim()
  const address = String(
    nested.address
    || meta.store_address
    || nested.formatted_address
    || nested.vicinity
    || item?.store_address
    || '',
  ).trim()
  const lat = firstFinite(nested.lat, nested.latitude, meta.latitude, item?.latitude)
  const lng = firstFinite(nested.lng, nested.longitude, meta.longitude, item?.longitude)
  const accuracy = firstFinite(nested.accuracy, meta.location_accuracy, item?.location_accuracy)
  const hasCoords = lat != null && lng != null
  if (!name && !address && !hasCoords) return null
  return {
    ...nested,
    name,
    address,
    lat,
    lng,
    accuracy,
    recordedAt: nested.recordedAt || meta.location_recorded_at || '',
    placeId: nested.placeId || nested.place_id || '',
    provider: nested.provider || (hasCoords ? 'gps' : ''),
    mode: nested.mode || (hasCoords && !(nested.placeId || nested.place_id) ? 'coords-only' : ''),
  }
}

export function readItemCapture(item) {
  const meta = readItemMeta(item)
  const nested = meta.images && typeof meta.images === 'object'
    ? (meta.images.capture || meta.images.original_capture)
    : ''
  const display = isDisplayableImageUrl(item?.capture_url) ? String(item.capture_url).trim() : ''
  const url = persistImageUrl(
    meta.capture_image_url
    || meta.original_capture_url
    || meta.store_photo_url
    || nested
    || '',
  ) || display
  if (!url) return null
  return {
    url,
    capturedAt: String(meta.captured_at || meta.capture?.observed_at || meta.observedAt || ''),
  }
}

export function buildGallerySlides(official, capture, { allowPreview = false } = {}) {
  const accept = allowPreview ? isDisplayableImageUrl : isPersistentImageUrl
  const first = accept(official) ? String(official).trim() : ''
  const second = accept(capture) ? String(capture).trim() : ''
  const slides = []
  if (first) slides.push({ src: first, label: OFFICIAL_LABEL })
  if (second && second !== first) slides.push({ src: second, label: CAPTURE_LABEL })
  return slides
}

function hasAnalysisCaptureMeta(item) {
  const meta = readItemMeta(item)
  const images = meta.images && typeof meta.images === 'object' ? meta.images : {}
  const identification = meta.identification && typeof meta.identification === 'object'
    ? meta.identification
    : {}
  const identSource = String(identification.source || meta.identification_source || '').trim().toLowerCase()
  const identTokens = identSource.split(/[+,]/).map((part) => part.trim()).filter(Boolean)
  return Boolean(
    meta.capture_image_url
    || meta.original_capture_url
    || meta.store_photo_url
    || meta.captured_at
    || meta.capture_image_key
    || images.capture
    || images.original_capture
    || images.capture_key
    || meta.capture?.original_uploaded_on_confirmation
    || identTokens.some((token) => ['vision', 'scan', 'photo', 'camera'].includes(token))
  )
}

export function readItemGallery(item) {
  const meta = readItemMeta(item)
  const images = meta.images && typeof meta.images === 'object' ? meta.images : {}
  const captureRefs = [
    meta.capture_image_url,
    meta.original_capture_url,
    meta.store_photo_url,
    meta.capture_image_key,
    images.capture,
    images.original_capture,
    images.capture_key,
  ]
  if (hasAnalysisCaptureMeta(item) && item?.image_key) {
    captureRefs.push(item.image_key)
  }
  const captureKeys = new Set(captureRefs.map(storageKeyFromRef).filter(Boolean))
  const officialCdn = persistImageUrl(meta.official_image_url || images.official || '')
  const itemUrl = isDisplayableImageUrl(item?.image_url) ? String(item.image_url).trim() : ''
  const itemKey = storageKeyFromRef(itemUrl) || storageKeyFromRef(item?.official_image_key)
  const itemIsCapture = Boolean(itemKey && captureKeys.has(itemKey))
  const officialUrl = officialCdn || (itemUrl && !itemIsCapture ? itemUrl : '')
  const capture = readItemCapture(item)?.url || ''
  const signedCapture = (itemUrl && itemIsCapture) ? itemUrl : ''
  const captureUrl = [capture, signedCapture].find((url) => url && url !== officialUrl) || ''
  const slides = buildGallerySlides(officialUrl, captureUrl, { allowPreview: true })
  const cardUrl = httpsDisplayUrl(officialUrl)
  return {
    official: officialUrl,
    capture: captureUrl,
    cardUrl,
    slides: slides.map((slide) => (
      slide.label === OFFICIAL_LABEL ? { ...slide, src: httpsDisplayUrl(slide.src) } : slide
    )),
  }
}

export function productStorageKey(item) {
  const officialKey = storageKeyFromRef(item?.official_image_key)
    || storageKeyFromRef(readItemMeta(item).official_image_key)
    || storageKeyFromRef(readItemMeta(item).images?.official_key)
  if (officialKey) return officialKey
  if (hasAnalysisCaptureMeta(item)) return ''
  return storageKeyFromRef(item?.image_key) || storageKeyFromRef(item?.image_url)
}

export function resolveCardImage(item) {
  const gallery = readItemGallery(item)
  const url = gallery.cardUrl || ''
  const key = productStorageKey(item)
  const signed = isSignedDisplayUrl(url)
  return {
    url,
    key,
    expiresIn: signed ? (Number(item?.image_expires_in) || 180) : 0,
    needsMediaFallback: !url && Boolean(key),
    isSigned: signed,
  }
}

export function buildItemMeta({
  barcode = '',
  brand = '',
  category = '',
  source = '',
  confidence = 0,
  observedAt = '',
  store = null,
  originalName = '',
  translatedName = '',
  model = '',
  variant = '',
  observedPrice = null,
  convertedPrice = null,
  currency = 'JPY',
  convertedCurrency = 'EUR',
  tax = null,
  images = null,
  capture = null,
  identification = null,
  exchangeRate = null,
  exchangeRateDate = '',
  officialImageMeta = null,
  officialImageKey = '',
  conversionStatus = '',
  conversionStale = false,
} = {}) {
  const observedAmount = observedPrice == null || observedPrice === ''
    ? null
    : Number(observedPrice)
  const convertedAmount = convertedPrice == null || convertedPrice === ''
    ? null
    : Number(convertedPrice)
  const taxIncluded = tax?.included == null ? null : Boolean(tax.included)
  const currencyCode = String(currency || 'JPY').toUpperCase()
  const isEur = currencyCode === 'EUR'
  const officialRaw = officialImageMeta?.url || images?.official || ''
  const officialUrl = persistImageUrl(officialRaw)
  const officialKey = String(
    officialImageKey
    || officialImageMeta?.key
    || images?.official_key
    || storageKeyFromRef(officialRaw)
    || '',
  ).trim()
  const captureUrl = persistImageUrl(images?.capture || capture?.url || '')
  const directConverted = isEur ? observedAmount : convertedAmount
  const meta = {
    barcode: String(barcode || '').trim(),
    brand: String(brand || '').trim(),
    category: String(category || '').trim(),
    source: String(source || '').trim(),
    confidence: Number(confidence) || 0,
    observedAt: observedAt || new Date().toISOString(),
    original_name: String(originalName || '').trim(),
    translated_name: String(translatedName || '').trim(),
    model: String(model || '').trim(),
    variant: String(variant || '').trim(),
    observed_price_jpy: isEur ? null : observedAmount,
    observed_price_eur: isEur ? observedAmount : directConverted,
    observed_currency: currencyCode,
    tax_included: taxIncluded,
    exchange_rate: isEur || exchangeRate == null || exchangeRate === '' ? null : Number(exchangeRate),
    converted_price_eur: directConverted,
    exchange_rate_date: isEur ? '' : String(exchangeRateDate || ''),
    conversion_status: String(
      conversionStatus
      || (isEur
        ? 'direct'
        : (convertedAmount > 0 && Number(exchangeRate) > 0 ? 'live' : convertedAmount > 0 ? 'cached' : 'pending')),
    ),
    conversion_stale: Boolean(conversionStale) && !isEur,
    official_image_url: officialUrl,
    official_image_key: officialKey,
    official_image_source_url: String(officialImageMeta?.source_url || ''),
    official_image_source_domain: String(officialImageMeta?.source_domain || officialImageMeta?.source_name || ''),
    official_image_retrieved_at: String(officialImageMeta?.retrieved_at || ''),
    capture_image_url: captureUrl,
    original_capture_url: captureUrl,
    store_photo_url: captureUrl,
    captured_at: String(capture?.observed_at || observedAt || new Date().toISOString()),
    identification_mode: String(identification?.mode || ''),
    identification_confidence: Number(identification?.confidence ?? confidence) || 0,
    identification_source: String(identification?.source || source || ''),
    price: {
      observed: observedAmount,
      currency: currencyCode,
      converted: directConverted,
      converted_currency: String(convertedCurrency || 'EUR').toUpperCase(),
      exchange_rate: isEur || exchangeRate == null || exchangeRate === '' ? null : Number(exchangeRate),
      exchange_rate_date: isEur ? '' : String(exchangeRateDate || ''),
      tax: tax && typeof tax === 'object'
        ? {
            status: String(tax.status || ''),
            included: tax.included == null ? null : Boolean(tax.included),
            rate: tax.rate == null || tax.rate === '' ? null : Number(tax.rate),
          }
        : null,
    },
    image_candidates: Array.isArray(images?.candidates)
      ? images.candidates.map(persistImageUrl).filter(Boolean)
      : [],
    images: images && typeof images === 'object'
      ? {
          official: officialUrl,
          official_key: officialKey,
          official_source_url: String(officialImageMeta?.source_url || ''),
          official_source_domain: String(officialImageMeta?.source_domain || officialImageMeta?.source_name || ''),
          capture: captureUrl,
          original_capture: captureUrl,
          additional: Array.isArray(images.additional)
            ? images.additional.map(persistImageUrl).filter(Boolean)
            : [],
          candidates: Array.isArray(images.candidates)
            ? images.candidates.map(persistImageUrl).filter(Boolean)
            : [],
        }
        : { official: officialUrl, official_key: officialKey, capture: captureUrl, additional: [], candidates: [] },
    capture: capture && typeof capture === 'object' ? { ...capture } : {},
    identification: identification && typeof identification === 'object' ? { ...identification } : {},
  }
  return store && typeof store === 'object' ? applyStoreToMeta(meta, store) : meta
}

export function certaintyFromConfidence(confidence) {
  const value = Number(confidence) || 0
  if (value >= 0.75) return 'Forte'
  if (value >= 0.45) return 'Moyenne'
  return 'Faible'
}
